import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  LocationOn,
  Business,
  AttachMoney,
  Settings,
} from '@mui/icons-material';
import apiService from '../../services/api';
import { Court, Club } from '../../types';

export const AdminCourtsPage: React.FC = () => {
  const [courts, setCourts] = useState<Court[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCourt, setEditingCourt] = useState<Court | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    club_id: '',
    is_active: true,
    is_external: false,
  });
  const [pricingData, setPricingData] = useState({
    hourly_rate: '',
    peak_hour_rate: '',
    weekend_rate: '',
    member_discount: '',
  });
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [selectedCourtForPricing, setSelectedCourtForPricing] = useState<Court | null>(null);

  useEffect(() => {
    fetchCourts();
    fetchClubs();
  }, []);

  const fetchCourts = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllCourts();
      console.log('Fetched courts:', response.data);
      // Ensure response.data is always an array
      const data: any = response.data;
      const courts = Array.isArray(data) ? data : (data?.courts || data?.data || []);
      setCourts(courts);
    } catch (error) {
      console.error('Error fetching courts:', error);
      setSnackbar({ open: true, message: 'Failed to fetch courts', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      const response = await apiService.getClubs();
      console.log('Fetched clubs:', response.data);
      // Ensure response.data is always an array
      const data: any = response.data;
      const clubs = Array.isArray(data) ? data : (data?.clubs || data?.data || []);
      setClubs(clubs);
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  };

  const handleCreateCourt = () => {
    setEditingCourt(null);
    setFormData({
      name: '',
      location: '',
      club_id: '',
      is_active: true,
      is_external: false,
    });
    setOpenDialog(true);
  };

  const handleEditCourt = (court: Court) => {
    setEditingCourt(court);
    setFormData({
      name: court.name,
      location: (court as any).location ?? '',
      club_id: court.club_id ? court.club_id.toString() : '',
      is_active: court.is_active,
      is_external: court.is_external ?? false,
    });
    setOpenDialog(true);
  };

  const handleSaveCourt = async () => {
    try {
      // Basic validation
      if (!formData.name.trim()) {
        setSnackbar({ open: true, message: 'Court name is required', severity: 'error' });
        return;
      }
      
      if (!formData.club_id || formData.club_id === '') {
        setSnackbar({ open: true, message: 'Please select a club', severity: 'error' });
        return;
      }

      const courtData = {
        ...formData,
        club_id: formData.club_id ? parseInt(formData.club_id, 10) : undefined,
        location: formData.location || undefined,
        is_external: formData.is_external,
      };
      
      if (editingCourt) {
        await apiService.updateCourt(editingCourt.id, courtData);
        setSnackbar({ open: true, message: 'Court updated successfully', severity: 'success' });
      } else {
        await apiService.createCourt(courtData);
        setSnackbar({ open: true, message: 'Court created successfully', severity: 'success' });
      }
      setOpenDialog(false);
      fetchCourts();
    } catch (error) {
      console.error('Error saving court:', error);
      setSnackbar({ open: true, message: 'Failed to save court', severity: 'error' });
    }
  };

  const handleDeleteCourt = async (courtId: number, cascade = false) => {
    const msg = cascade
      ? 'This will also delete all matches on this court. Continue?'
      : 'Are you sure you want to delete this court?';
    if (!window.confirm(msg)) return;
    try {
      await apiService.deleteCourt(courtId, cascade);
      setSnackbar({ open: true, message: 'Court deleted successfully', severity: 'success' });
      fetchCourts();
    } catch (error: any) {
      const detail = error?.response?.data?.detail || '';
      if (!cascade && typeof detail === 'string' && detail.includes('match(es)')) {
        if (window.confirm('This court has matches. Delete the court and all its matches?')) {
          handleDeleteCourt(courtId, true);
        }
      } else {
        setSnackbar({ open: true, message: detail || 'Failed to delete court', severity: 'error' });
      }
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePricingChange = (field: string, value: any) => {
    setPricingData(prev => ({ ...prev, [field]: value }));
  };

  const handleOpenPricingDialog = (court: Court) => {
    setSelectedCourtForPricing(court);
    setPricingData({
      hourly_rate: (court as any).hourly_rate?.toString() || '',
      peak_hour_rate: (court as any).peak_hour_rate?.toString() || '',
      weekend_rate: (court as any).weekend_rate?.toString() || '',
      member_discount: (court as any).member_discount?.toString() || '',
    });
    setPricingDialogOpen(true);
  };

  const handleSavePricing = async () => {
    if (!selectedCourtForPricing) return;

    try {
      await apiService.updateCourtPricing(selectedCourtForPricing.id, {
        hourly_rate: parseFloat(pricingData.hourly_rate),
        peak_hour_rate: parseFloat(pricingData.peak_hour_rate),
        weekend_rate: parseFloat(pricingData.weekend_rate),
        member_discount: parseFloat(pricingData.member_discount),
      });
      
      setSnackbar({ open: true, message: 'Court pricing updated successfully', severity: 'success' });
      setPricingDialogOpen(false);
      fetchCourts();
    } catch (error) {
      console.error('Error updating court pricing:', error);
      setSnackbar({ open: true, message: 'Failed to update court pricing', severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Court Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateCourt}
        >
          Add Court
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <LocationOn color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{courts.length}</Typography>
                  <Typography color="textSecondary">Total Courts</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <LocationOn color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {courts.filter(court => court.is_active).length}
                  </Typography>
                  <Typography color="textSecondary">Active Courts</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Business color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{clubs.length}</Typography>
                  <Typography color="textSecondary">Total Clubs</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <LocationOn color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {courts.length}
                  </Typography>
                  <Typography color="textSecondary">Total Courts</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Courts Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Club</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Pricing</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {courts.map((court) => (
                <TableRow key={court.id}>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {court.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Business fontSize="small" sx={{ mr: 1 }} />
                      {court.club_name || (() => {
                        const club = clubs.find(club => club.id === court.club_id);
                        return club?.name || `Unknown Club (ID: ${court.club_id})`;
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(court as any).is_external ? 'External' : 'Internal'}
                      color={(court as any).is_external ? 'primary' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AttachMoney fontSize="small" />
                      <Typography variant="body2">
                        ${(court as any).hourly_rate || 'N/A'}/hr
                      </Typography>
                    </Box>
                    {(court as any).peak_hour_rate && (
                      <Typography variant="caption" color="text.secondary">
                        Peak: ${(court as any).peak_hour_rate}/hr
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={court.is_active ? 'Active' : 'Inactive'}
                      color={court.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Edit Court">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleEditCourt(court)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Manage Pricing">
                        <IconButton 
                          size="small" 
                          color="secondary"
                          onClick={() => handleOpenPricingDialog(court)}
                        >
                          <Settings />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Court">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteCourt(court.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCourt ? 'Edit Court' : 'Create New Court'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              label="Court Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
            />
            <TextField
              fullWidth
              label="Location (optional)"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="e.g. Building A, Court 1"
            />
            <FormControl fullWidth required>
                <InputLabel>Club</InputLabel>
                <Select
                  value={formData.club_id}
                  label="Club"
                  onChange={(e) => handleInputChange('club_id', e.target.value)}
                >
                  {clubs.map((club) => (
                    <MenuItem key={club.id} value={club.id.toString()}>
                      {club.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_external}
                  onChange={(e) => handleInputChange('is_external', e.target.checked)}
                  color="primary"
                />
              }
              label="External court (weather suitability shown for external courts)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveCourt} variant="contained">
            {editingCourt ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pricing Dialog */}
      <Dialog open={pricingDialogOpen} onClose={() => setPricingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Manage Pricing - {selectedCourtForPricing?.name}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Hourly Rate ($)"
              type="number"
              value={pricingData.hourly_rate}
              onChange={(e) => handlePricingChange('hourly_rate', e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              label="Peak Hour Rate ($)"
              type="number"
              value={pricingData.peak_hour_rate}
              onChange={(e) => handlePricingChange('peak_hour_rate', e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Rate during peak hours (e.g., evenings, weekends)"
            />
            <TextField
              label="Weekend Rate ($)"
              type="number"
              value={pricingData.weekend_rate}
              onChange={(e) => handlePricingChange('weekend_rate', e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Rate for weekend bookings"
            />
            <TextField
              label="Member Discount (%)"
              type="number"
              value={pricingData.member_discount}
              onChange={(e) => handlePricingChange('member_discount', e.target.value)}
              fullWidth
              inputProps={{ min: 0, max: 100, step: 1 }}
              helperText="Percentage discount for club members"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPricingDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSavePricing} variant="contained">
            Save Pricing
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
